import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap, catchError, map, timeout } from 'rxjs/operators';
import { keysToCamel } from './utils/case.util';
import { jwtDecode } from 'jwt-decode';
import { AuthAuditService } from './services/auth-audit.service';

/** Enum of known roles for type-safe role checks. */
export enum AuthRole {
  Admin = 'Admin',
  Supplier = 'Supplier',
  User = 'User'
}

export interface AuthUser {
  id: string;
  email: string;
  roles: AuthRole[];
  fornecedorId?: string;
}


export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private accessToken: string | null = null;
  private userSubject = new BehaviorSubject<AuthUser | null>(null);
  public user$ = this.userSubject.asObservable();

  // Mantido para compatibilidade com guards — sempre emite false imediatamente.
  private initializingSubject = new BehaviorSubject<boolean>(false);
  public initializing$ = this.initializingSubject.asObservable();

  private isLoggedOutSubject = new BehaviorSubject<boolean>(false);
  public isLoggedOut$ = this.isLoggedOutSubject.asObservable();

  private refreshTokenKey = 'gn_refresh_token';
  private accessTokenKey = 'gn_access_token';
  /** Usuário serializado — não depende de claims do JWT para restaurar roles no boot. */
  private userKey = 'gn_user';

  constructor(private http: HttpClient, private audit: AuthAuditService) {
    // Sincronizar logout entre abas
    window.addEventListener('storage', (event) => {
      if (event.key === this.refreshTokenKey && event.newValue === null) {
        this.logout('other tab logged out');
      }
    });

    // Restaurar sessão sincronamente — sem chamadas de rede, sem bloqueio.
    // Se não há credenciais válidas, os guards redirecionam para /auth/login
    // e o LoginComponent cuida do refresh silencioso quando necessário.
    this.restoreSession();
  }

  /**
   * Restaura sessão sincronamente a partir do storage.
   * Não faz chamadas de rede.
   */
  private restoreSession(): void {
    const at = sessionStorage.getItem(this.accessTokenKey);

    if (!at || this.isTokenExpired(at)) {
      if (at) sessionStorage.removeItem(this.accessTokenKey);
      console.debug('[AuthService] No valid access token on boot');
      return;
    }

    const userStr = localStorage.getItem(this.userKey);
    if (!userStr) {
      // Access token presente mas sem gn_user no localStorage.
      // Tratar como sessão inválida: guard redireciona para /auth/login
      // e o LoginComponent faz refresh silencioso para reobter o usuário.
      sessionStorage.removeItem(this.accessTokenKey);
      console.warn('[AuthService] Access token found but no stored user — clearing token, will refresh on login');
      return;
    }

    try {
      const user = JSON.parse(userStr) as AuthUser;
      this.accessToken = at;
      this.userSubject.next(user);
      console.debug('[AuthService] Session restored:', user.email, '| roles:', user.roles);
    } catch {
      console.warn('[AuthService] Corrupted gn_user in localStorage — clearing session');
      localStorage.removeItem(this.userKey);
      sessionStorage.removeItem(this.accessTokenKey);
    }
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Verifica se um token JWT está expirado
   */
  private isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    try {
      const decoded: any = jwtDecode(token);
      const expiryTime = decoded.exp * 1000; // Converter para milissegundos
      const isExpired = Date.now() >= expiryTime;
      if (isExpired) {
        console.debug('[AuthService] Token expirado:', new Date(expiryTime));
      }
      return isExpired;
    } catch (error) {
      console.debug('[AuthService] Erro ao decodificar token:', error);
      return true; // Token inválido = tratado como expirado
    }
  }

  /**
   * Verifica se o usuário está autenticado
   * Valida não apenas existência do token, mas também sua expiração
   */
  isAuthenticated(): boolean {
    const hasToken = !!this.accessToken;
    const tokenValid = !this.isTokenExpired(this.accessToken);
    return hasToken && tokenValid;
  }

  hasRole(role: string): boolean {
    const roles = (this.userSubject.value?.roles || []).map(r => r.toLowerCase());
    return roles.includes(role.toLowerCase());
  }

  login(email: string, password: string): Observable<LoginResponse> {
    const apiBase = environment.API_BASE_URL;
    return this.http.post<LoginResponse>(`${apiBase}/auth/login`, { email, password }).pipe(
      timeout(15000), // 15 segundos timeout
      map(res => keysToCamel(res)),
      tap(res => {
        console.debug('[AuthService] login response:', res);
        if (!res.refreshToken) {
          console.error('[AuthService] login response missing refreshToken:', res);
        }
        this.setAccessToken(res.accessToken);
        try {
          localStorage.setItem(this.refreshTokenKey, res.refreshToken);
          console.debug('[AuthService] refreshToken salvo no localStorage:', res.refreshToken);
        } catch (e) {
          console.error('[AuthService] Falha ao salvar refreshToken no localStorage:', e);
        }
        const user = this.normalizeUser(res.user);
        this.persistUser(user);
        this.updateUser(user);
        this.audit.record('login_success', { userId: res.user?.id, email: res.user?.email });
        console.debug('[AuthService] login successful, tokens stored');
      }),
      catchError(err => {
        console.error('[AuthService] login error', err);
        this.audit.record('login_failed', { reason: err?.message || String(err) });
        return throwError(() => err);
      })
    );
  }

  logout(reason?: string): void {
    console.debug('[AuthService] logout executed', reason ? `| reason: ${reason}` : '');
    this.audit.record('logout', { reason, userId: this.userSubject.value?.id, email: this.userSubject.value?.email });
    this.accessToken = null;
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
    sessionStorage.removeItem(this.accessTokenKey);
    this.userSubject.next(null);
    this.isLoggedOutSubject.next(true);
    setTimeout(() => this.isLoggedOutSubject.next(false), 100);
  }

  /**
   * Atualiza o usuário autenticado no subject.
   */
  public updateUser(user: AuthUser | null): void {
    this.userSubject.next(user);
  }

  /**
   * @param silent Se true, não chama logout em caso de falha.
   *               Use true para refreshes em background onde o access token ainda é válido.
   */
  refresh(silent = false): Observable<LoginResponse> {
    const rt = localStorage.getItem(this.refreshTokenKey);
    if (!rt) return throwError(() => new Error('No refresh token'));
    const apiBase = environment.API_BASE_URL;
    return this.http.post<LoginResponse>(`${apiBase}/auth/refresh`, { refreshToken: rt }).pipe(
      timeout(15000),
      map(res => keysToCamel(res)),
      tap(res => {
        this.setAccessToken(res.accessToken);
        localStorage.setItem(this.refreshTokenKey, res.refreshToken);
        const user = this.normalizeUser(res.user);
        this.persistUser(user);
        this.updateUser(user);
        this.audit.record('token_refresh_success', { userId: res.user?.id });
        console.debug('[AuthService] refresh succeeded, tokens updated');
      }),
      catchError(err => {
        console.error('[AuthService] refresh failed:', err);
        this.audit.record('token_refresh_failed', { reason: err?.message || String(err) });
        if (!silent) {
          this.logout('token refresh failed');
        }
        return throwError(() => err);
      })
    );
  }

  get currentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  private normalizeUser(user: any): AuthUser {
    if (!user) return { id: '', email: '', roles: [] };
    // Suportar tanto 'roles' (array/string) quanto 'role' (singular) que a API pode retornar
    const rolesRaw = user.roles ?? user.role;
    let roles: AuthRole[] = [];
    if (Array.isArray(rolesRaw)) {
      roles = rolesRaw
        .filter((r): r is string => typeof r === 'string')
        .map(r => r.trim())
        .filter(r => Object.values(AuthRole).includes(r as AuthRole))
        .map(r => r as AuthRole);
    } else if (typeof rolesRaw === 'string') {
      roles = rolesRaw
        .split(',')
        .map(r => r.trim())
        .filter(r => Object.values(AuthRole).includes(r as AuthRole))
        .map(r => r as AuthRole);
    }
    return { ...user, id: user.id || '', email: user.email || '', roles } as AuthUser;
  }

  /** Persiste o usuário no localStorage para restauração síncrona no boot. */
  private persistUser(user: AuthUser): void {
    try {
      localStorage.setItem(this.userKey, JSON.stringify(user));
    } catch (e) {
      console.warn('[AuthService] Failed to persist user', e);
    }
  }

  private setAccessToken(token: string | null) {
    this.accessToken = token;
    if (token) {
      try {
        sessionStorage.setItem(this.accessTokenKey, token);
      } catch (e) {
        console.warn('[AuthService] Failed to persist access token to sessionStorage', e);
      }
    } else {
      sessionStorage.removeItem(this.accessTokenKey);
    }
  }

}

